import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { startScreenTracking } from "services/monitoring";

// Human-readable names for Sentry Performance transaction names.
const SCREEN_NAMES: Record<string, string> = {
  "/appointments": "Appointments",
  "/patients": "Patient List",
  "/encounters": "Open Encounters",
  "/settings": "Settings",
  "/annotations": "Annotations",
  "/login": "Login",
};

function getScreenName(pathname: string): string {
  if (SCREEN_NAMES[pathname]) return SCREEN_NAMES[pathname];
  if (/\/patients\/[^/]+\/interimScreen/.test(pathname)) return "End Visit";
  if (/\/schedule\/patient\//.test(pathname)) return "Scheduled Patient Detail";
  if (/\/patients\/[^/]+/.test(pathname)) return "Patient Detail";
  return pathname;
}

/**
 * Tracks how long the current user spends on each screen.
 * On every route change, ends the span for the previous screen (recording its duration)
 * and starts a new span for the incoming screen.
 *
 * The resulting spans appear in Sentry Performance under op:"ui.screen" and can be
 * queried in custom Sentry dashboards with:
 *   - transaction.op:ui.screen
 *   - group by: transaction (screen name)
 *   - aggregate: avg(transaction.duration) or p75(transaction.duration)
 *
 * Must be rendered inside <BrowserRouter>.
 */
export function useScreenTimeTracking(): void {
  const location = useLocation();
  const stopTrackingRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const screenName = getScreenName(location.pathname);
    stopTrackingRef.current = startScreenTracking(screenName);
    return () => {
      stopTrackingRef.current?.();
      stopTrackingRef.current = null;
    };
  }, [location.pathname]);
}
